import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LucideIcon } from 'lucide-react-native';
import { memo } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import { aiColors } from '../theme';

/** The one action a card is for: filled, full width. */
export const PrimaryButton = memo(
  ({
    title,
    icon: Icon,
    loading,
    disabled,
    onPress,
  }: {
    title: string;
    icon?: LucideIcon;
    loading?: boolean;
    disabled?: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const inactive = disabled || loading;
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: !!inactive, busy: !!loading }}
        disabled={inactive}
        onPress={onPress}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(8),
          height: rpx(42),
          borderRadius: rpx(11),
          backgroundColor: aiColors.primary,
          opacity: disabled ? 0.45 : pressed ? 0.85 : 1,
        })}
      >
        {loading ? (
          <ActivityIndicator size="small" color={aiColors.primaryForeground} />
        ) : Icon ? (
          <Icon size={16} color={aiColors.primaryForeground} />
        ) : null}
        <Typography.Text
          medium
          size={rpx(14)}
          color={aiColors.primaryForeground}
        >
          {title}
        </Typography.Text>
      </Pressable>
    );
  },
);

/** A secondary action: outlined, sized by its row. */
export const SecondaryButton = memo(
  ({
    title,
    icon: Icon,
    loading,
    disabled,
    onPress,
  }: {
    title: string;
    icon?: LucideIcon;
    loading?: boolean;
    disabled?: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const inactive = disabled || loading;
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: !!inactive, busy: !!loading }}
        disabled={inactive}
        onPress={onPress}
        style={({ pressed }) => ({
          // Grows along a row; in a column it keeps its own height.
          flexGrow: 1,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(6),
          height: rpx(40),
          borderRadius: rpx(11),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          opacity: disabled ? 0.45 : pressed ? 0.7 : 1,
        })}
      >
        {loading ? (
          <ActivityIndicator size="small" color={aiColors.primary} />
        ) : Icon ? (
          <Icon size={14} color={aiColors.foreground} />
        ) : null}
        <Typography.Text size={rpx(13)} color={aiColors.foreground}>
          {title}
        </Typography.Text>
      </Pressable>
    );
  },
);
